import {Injectable} from '@angular/core';
import {CalculationService} from '../../calculation.service';
import {AgreementsSectionInterface} from '../../../api/sections-api/agreements-section.interface';
import {CalculationStorageService} from '../../../../../shared/services/calculation-storage.service';
import {AgreementModel} from '../../../../models/agreement.model';
import {Offer} from '../../../../models/offer/offer.model';
import {OwnerDataModel} from '../../../../models/owner-data/owner-data.model';
import {VehicleModelState} from '../../../../../shared/states/vehicle-model.state';
import {VehicleDataModel} from '../../../../models/vehicle-data.model';
import {RegistrationService} from '../../registration.service';
import {VehicleModelStateService} from '../vehicle-model/vehicle-model-state.service';
import {ApplicationStateService} from '../../../../../shared/services/application-state.service';
import {ConfigurationService} from '../../configuration/configuration.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {isBoolean, isNullOrUndefined} from 'util';
import {b64ToUtf8, noop} from '../../../../utils/utils';
import {fcall} from 'q';
import {BehaviorSubject} from 'rxjs/Rx';
import {AgreementsService} from '../../../../../shared/services/agreements.service';
import {SelectedOfferService} from '../selected-offer/selected-offer.service';
import {ResourceService} from '../../../../../shared/services/resource.service';
import {StepKey} from '../../../../enums/step-key.enum';

@Injectable()
export class AgreementsStateService implements AgreementsSectionInterface {

  $initCompleted: BehaviorSubject<boolean> = new BehaviorSubject(false);
  offerAgreements: AgreementModel[] = [];
  private _agreementsSection: AgreementsSectionInterface;
  private _offer: Offer;
  private promises: any[];
  private agreementsOrder: string[] = []; // agreements codes array (offer + destination)

  constructor(private calculation: CalculationService,
              private applicationState: ApplicationStateService,
              private selectedOfferService: SelectedOfferService, // required for agreement visibility conditions based on selected offer checking
              private calculationStorage: CalculationStorageService,
              private registration: RegistrationService,
              private configuration: ConfigurationService,
              private agreementsService: AgreementsService,
              private resourceService: ResourceService,
              private vehicleModelStateService: VehicleModelStateService) {
  }

  init() {
    this.initOfferAgreements();
  };

  get selectedOffer(): Offer {
    return this.applicationState.selectedOffer;
  }

  initOfferAgreements(): void {
    if (this.registration.agreements && this.registration.agreements.length > 0) {
      this.offerAgreements = this.registration.agreements;
      this.$initCompleted.next(true);
    } else {
      this.pushAllAgreement();
    }
    this.fixResourcesEndpoints();
  }

  pushAllAgreement(): void {
    this.offerAgreements = [];
    this.promises = [];

    const agreementsForDestination = this.agreementsService.getAgreementsForDestination(StepKey.THIRD_STEP);
    if (this.selectedOffer.agreements) {
      this.agreementsOrder.push(
        ...this.selectedOffer.agreements.map(agreement => agreement.code),
        ...agreementsForDestination.map(agreement => agreement.code)
      );
      this.selectedOffer.agreements.forEach((agreement: AgreementModel) => this.processAgreement(agreement));
    }

    if (agreementsForDestination) {
      agreementsForDestination.forEach((agreement: AgreementModel) => this.processAgreement(agreement));
    }

    if (this.promises.length > 0) {
      Promise.all(this.promises).then(() => {
        this.offerAgreements.sort((a1, a2) => this.agreementsOrder.indexOf(a1.code) - this.agreementsOrder.indexOf(a2.code)); // sort agreements after async agreements were added - using original order
        this.$initCompleted.next(true);
      });
    } else {
      this.$initCompleted.next(true);
    }

    this.registration.agreements = this.offerAgreements;
  }

  fcallf(agreement) {
    return fcall(eval('(' + b64ToUtf8(<string>agreement.details.visible) + ')'), this)
      .then(value => {
        if (value) {
          this.offerAgreements.push(agreement);
        }
      });
  }

  get offer(): Offer {
    return this._offer;
  }

  set offer(value: Offer) {
    this._offer = value;
  }

  get agreementsSection(): AgreementsSectionInterface {
    return this._agreementsSection;
  }

  set agreementsSection(value: AgreementsSectionInterface) {
    this._agreementsSection = value;
  }

  get ownerData(): OwnerDataModel {
    return this.calculation.ownerData;
  }

  get vehicleModelState(): VehicleModelState {
    return this.vehicleModelStateService.vehicleModelState;
  }

  get vehicleData(): VehicleDataModel {
    return this.calculation.vehicleData;
  }

  changeAgreement(code: string, selected: boolean): void {
    const selectedAnswer = this.getSelectedAgreementAnswer(code, selected);
    this.getAgreement(code).selectedAnswer = selectedAnswer;
    this.registration.updateAgreement(code, selected, selectedAnswer);
  }

  private fixResourcesEndpoints(): void {
    if (this.resourceService.isResourceEndpointCustom) {
      this.offerAgreements.forEach(agreement => {
        agreement.details.values.forEach(agreementValue => {
          if (agreementValue && agreementValue.value) {
            const regexp = new RegExp(this.resourceService.configResourcesEndpoint, 'g');
            agreementValue.value = agreementValue.value.replace(regexp, this.resourceService.resourcesEndpoint);
          }
        });
      });
    }
  }

  private processAgreement(agreement: AgreementModel): void {
    if (!isNullOrUndefined(agreement.details.visible)) {
      if (isBoolean(agreement.details.visible)) {
        agreement.details.visible ? this.offerAgreements.push(agreement) : noop();
      } else {
        this.promises.push(this.fcallf(agreement));
      }
    } else {
      this.offerAgreements.push(agreement);
    }
  }

  private getAgreement(code: string): AgreementModel {
    return this.offerAgreements.filter(item => item.code === code)[0];
  }

  private getSelectedAgreementAnswer(code: string, selected: boolean): string {
    const answers = this.getAgreement(code).answers;
    const suffix = selected ? 'YES' : 'NO';
    return answers.find(answer => answer.code.endsWith(suffix)).code;
  }

  private addLinksToAgreements(): void {
    this.offerAgreements.forEach(agreement => {
      const links = agreement.details.values[0].value.match(/<a href="(.*?)">/g);
      if (isNotNullOrUndefined(links)) {
        const link = links[0];
        const parts = link.split('""');
        // const result = parts[0] + this.resourceService.buildResourceURL(this.getResource(agreement.code)) + parts[1];
      }
    })
  }

}
